import {useApolloClient} from '@apollo/client';
import {none, some} from '@danwang/option';
import {
  FieldType,
  TransferType,
  useAllAccountsQuery,
  useAllFieldsQuery,
  useBindingsQuery,
  useCreateRawTransactionMutation,
  useCreateTransactionMutation,
  useDeleteRawTransactionsMutation,
  useSetBindingMutation,
} from '@transactions/graphql';
import * as Date from 'common/date';
import Money from 'common/Money';
import * as React from 'react';
import {AccountInlineDisplay} from 'src/AccountInlineDisplay';
import AccountSelect from 'src/AccountSelect';
import {isAccountClosed} from 'src/AccountsView/accountFilter';
import {AmountDisplay} from 'src/AmountDisplay';
import {Button} from 'src/Button';
import DatePicker from 'src/DatePicker/DatePicker';
import InputGroup from 'src/InputGroup';
import {TextInput} from 'src/inputs/TextInput';
import {INVERSE_PRESETS} from 'src/inversePresets';
import {Modal} from 'src/Modal';
import type {Option} from '@danwang/option';
import type {
  AccountListItemFragment,
  BasicRawTransactionFragment,
} from '@transactions/graphql';
import type {DateTime} from 'luxon';
import type {InversePreset} from 'src/inversePresets';
import type {SystemProps} from 'src/Modals';

export type CreateInverseRawTransactionModalUserProps = {
  /**
   * One or more rows, all in one currency (the opener enforces this); the
   * created inverse offsets their combined net.
   */
  rawTransactions: BasicRawTransactionFragment[];
  onCreate?: (data: {
    created: BasicRawTransactionFragment;
    /**
     * Present when the sources were combined with the inverse into a
     * transfer — every listed row is now fully consumed.
     */
    consumedSourceIds?: string[];
  }) => void;
};

// The sources are read-only context, not form fields, so they render as a
// card above the form rather than as InputGroup rows. Descriptions are
// frequently long (raw bank descriptors), so the card wraps them across
// lines instead of letting them run off the modal. With several rows the
// card totals them, since the inverse offsets the net, not any single row.
const SourceCard = ({
  rawTransactions,
  net,
}: {
  rawTransactions: BasicRawTransactionFragment[];
  net: Money;
}) => (
  <div className="border-border bg-muted/40 flex flex-col gap-2 rounded-md border px-3 py-2.5">
    <div className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
      {rawTransactions.length === 1
        ? 'Source transaction'
        : 'Source transactions'}
    </div>
    {rawTransactions.map((rawTransaction) => (
      <div key={rawTransaction._id} className="flex flex-col gap-1">
        <div className="flex flex-row items-start justify-between gap-3">
          <div className="text-foreground min-w-0 flex-1 text-sm break-words">
            {rawTransaction.description}
          </div>
          <div className="shrink-0 text-sm">
            <AmountDisplay
              variant="plain"
              amount={Money.fromInt(
                rawTransaction.amount,
                rawTransaction.currency,
              )}
            />
          </div>
        </div>
        <div className="text-muted-foreground flex flex-row items-center justify-between gap-3 text-sm">
          <AccountInlineDisplay account={rawTransaction.account} />
          <span className="shrink-0 tabular-nums">{rawTransaction.date}</span>
        </div>
      </div>
    ))}
    {rawTransactions.length > 1 && (
      <div className="border-border flex flex-row items-center justify-between gap-3 border-t pt-2 text-sm">
        <span className="text-foreground font-medium">Net</span>
        <AmountDisplay variant="plain" amount={net} />
      </div>
    )}
  </div>
);

export const CreateInverseRawTransactionModal = (
  props: CreateInverseRawTransactionModalUserProps & SystemProps,
) => {
  const formId = React.useId();
  const combineId = React.useId();
  const {onRequestClose, onCreate, rawTransactions} = props;
  const client = useApolloClient();

  const currency = rawTransactions[0].currency;
  const net = rawTransactions.reduce((sum, r) => sum + r.amount, 0);
  const inverseAmount = Money.fromInt(-net, currency);

  // The largest leg is the flow's identity (an ATM withdrawal next to its
  // fee refund), so its description seeds the form; the earliest date is
  // when the counterpart actually happened (the withdrawal, not a refund
  // that posted weeks later).
  const primary = rawTransactions.reduce((a, b) =>
    Math.abs(b.amount) > Math.abs(a.amount) ? b : a,
  );
  const earliestDate = rawTransactions.reduce((a, b) =>
    b.date < a.date ? b : a,
  ).date;

  const [account, setAccount] =
    React.useState<Option<AccountListItemFragment>>(none);
  const [date, setDate] = React.useState<DateTime>(Date.parseDate(earliestDate));
  const [description, setDescription] = React.useState(primary.description);
  const [combine, setCombine] = React.useState(false);
  const [activePreset, setActivePreset] = React.useState<InversePreset | null>(
    null,
  );
  const [error, setError] = React.useState<string | null>(null);

  const {data: bindingsData} = useBindingsQuery();
  // Same query AccountSelect runs, so this is served from the cache; used to
  // resolve a preset's bound account id to the fragment the picker holds.
  const {data: accountsData} = useAllAccountsQuery();

  const [createRawTransaction, {loading: creatingRaw}] =
    useCreateRawTransactionMutation();
  const [createTransaction, {loading: creatingTransfer}] =
    useCreateTransactionMutation();
  const [deleteRawTransactions, {loading: rollingBack}] =
    useDeleteRawTransactionsMutation();
  const [setBinding] = useSetBindingMutation();
  const loading = creatingRaw || creatingTransfer || rollingBack;

  // Combining consumes each source in full, and the server rejects a raw
  // that already has consumptions — the same rule as "Add to transaction" —
  // as well as any zero-amount raw (nothing to consume), so a zero row in
  // the selection would fail the transfer after the inverse was created.
  const sourcesCombinable = rawTransactions.every(
    (r) => r.amount !== 0 && (r.remainingAmount ?? r.amount) === r.amount,
  );

  // A foreign transfer must land in ONE account — the cost it carries
  // becomes a single basis lot (validateTransferLegs) — so a positive
  // source leg (a refund) in one account plus the inverse landing in
  // another would be rejected. Gate the combine here instead of letting
  // submit create the raw and roll it back. USD transfers carry no lot and
  // may span destinations freely.
  const positiveDestinations = new Set(
    rawTransactions.filter((r) => r.amount > 0).map((r) => r.account._id),
  );
  account.forEach((selectedAccount) => {
    if (-net > 0) {
      positiveDestinations.add(selectedAccount._id);
    }
  });
  const foreignSplitDestination =
    currency !== 'USD' && positiveDestinations.size > 1;

  // The transfer submits no field values, and validateFields rejects a
  // missing required enum field — so with one configured, combining could
  // only fail after the raw was created. This modal deliberately has no
  // field inputs (the full create-transaction flow does); it gates the
  // checkbox instead. Matches the server's rule: only enum fields can be
  // required.
  const {data: fieldsData} = useAllFieldsQuery();
  const requiredFields = (fieldsData?.allFields ?? []).filter(
    (field) => field.required && field.type === FieldType.Enum,
  );

  const canCombine =
    // An unresolved fields query is not a confirmed-empty required set — on
    // a cold cache the combine stays down until AllFields answers, or a
    // fast submit could slip past the gate it exists for.
    fieldsData != null &&
    sourcesCombinable &&
    !foreignSplitDestination &&
    requiredFields.length === 0;
  // The stored checkbox state survives a transient invalid account pick;
  // only what submit actually does must respect the gate.
  const effectiveCombine = combine && canCombine;

  const presets = INVERSE_PRESETS.filter((preset) => preset.appliesTo(net));
  // A preset resolves its bound account from these queries at click time,
  // so it stands down until both have answered — clicking earlier would
  // silently skip the prefill it advertises.
  const presetsReady = bindingsData != null && accountsData != null;

  const applyPreset = (preset: InversePreset) => {
    setActivePreset(preset);
    const boundId = bindingsData?.bindings.find(
      (binding) => binding.key === preset.accountBinding,
    )?.entityId;
    const bound = accountsData?.allAccounts.find(
      // A closed or cross-currency bound account cannot receive the row;
      // leave the picker for the user rather than prefill a doomed choice.
      (candidate) =>
        candidate._id === boundId &&
        !isAccountClosed(candidate) &&
        candidate.currency === currency,
    );
    if (bound) {
      setAccount(some(bound));
    }
    setDescription(preset.description);
    if (sourcesCombinable) {
      setCombine(true);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    account.forEach((selectedAccount) => {
      const run = async () => {
        setError(null);
        const rawResult = await createRawTransaction({
          variables: {
            props: {
              accountId: selectedAccount._id,
              amount: -net,
              currency,
              date: date.toISODate() ?? '',
              description,
            },
          },
        });
        const created = rawResult.data?.createRawTransaction;
        if (!created) return;

        if (effectiveCombine) {
          try {
            await createTransaction({
              variables: {
                rawTransactions: [
                  ...rawTransactions.map((r) => r._id),
                  created._id,
                ],
                date: date.toISODate() ?? '',
                // The transfer renders its own description as the row title,
                // so it carries the same one the inverse raw was given.
                description,
                transferType:
                  activePreset?.transferType ?? TransferType.AccountTransfer,
              },
            });
          } catch (err) {
            const transferError =
              err instanceof Error ? err.message : 'Transfer failed';
            // Roll back the inverse raw so a failed transfer leaves the
            // target account untouched instead of holding a stray row. The
            // rollback itself can fail (connectivity loss — or the transfer
            // actually committed and its response was lost, which makes the
            // raw consumed and undeletable); surface that state instead of
            // letting the error escape with the raw silently persisted.
            try {
              await deleteRawTransactions({variables: {ids: [created._id]}});
              setError(transferError);
            } catch {
              setError(
                `${transferError} — and the created inverse row could not ` +
                  'be rolled back. Check the target account before retrying: ' +
                  'the row may still exist, or the transfer may actually ' +
                  'have gone through.',
              );
            }
            return;
          }
        }

        if (activePreset) {
          // Remember the preset's account for next time (the same
          // auto-remember the import modal uses). The flow has already
          // succeeded, so a failure here is not worth surfacing.
          const current = bindingsData?.bindings.find(
            (binding) => binding.key === activePreset.accountBinding,
          )?.entityId;
          if (current !== selectedAccount._id) {
            setBinding({
              variables: {
                key: activePreset.accountBinding,
                entityId: selectedAccount._id,
              },
            })
              .then(() => {
                client.cache.evict({fieldName: 'bindings'});
              })
              .catch(() => {});
          }
        }

        onRequestClose();
        onCreate?.({
          // When combined, the created row is already fully consumed by the
          // transfer — report it that way so lists don't show a stale
          // remainder.
          created: effectiveCombine ? {...created, remainingAmount: 0} : created,
          consumedSourceIds: effectiveCombine
            ? rawTransactions.map((r) => r._id)
            : undefined,
        });
      };
      void run();
    });
  };

  return (
    <Modal
      onRequestClose={onRequestClose}
      size="md"
      header="Create inverse transaction"
      footer={
        <>
          <Button variant="outline" onClick={onRequestClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={account.isEmpty || loading}
            form={formId}
          >
            Create
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <SourceCard
          rawTransactions={rawTransactions}
          net={Money.fromInt(net, currency)}
        />
        <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-2">
          {presets.length > 0 && (
            <InputGroup label="Shortcuts">
              <div className="flex flex-wrap gap-2">
                {presets.map((preset) => (
                  <Button
                    key={preset.key}
                    variant={
                      activePreset?.key === preset.key ? 'primary' : 'outline'
                    }
                    className="px-3 py-1 text-sm"
                    disabled={!presetsReady}
                    onClick={() => applyPreset(preset)}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </InputGroup>
          )}
          <InputGroup label="Inverse amount">
            {/* h-9 matches the inputs below, so every row is the same height
                and the labels stay evenly spaced down the form */}
            <div className="flex h-9 items-center">
              <AmountDisplay variant="plain" align="left" amount={inverseAmount} />
            </div>
          </InputGroup>
          <InputGroup label="Target account">
            <AccountSelect
              selectedAccount={account}
              onSelectAccount={setAccount}
              currency={currency}
            />
          </InputGroup>
          <InputGroup label="Date">
            <DatePicker selectedDate={date} onChange={setDate} />
          </InputGroup>
          <InputGroup label="Description">
            <TextInput
              value={description}
              onChange={setDescription}
              placeholder="Description"
            />
          </InputGroup>
          <div className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              id={combineId}
              checked={effectiveCombine}
              disabled={!canCombine}
              onChange={(e) => setCombine(e.target.checked)}
              className="h-4 w-4"
            />
            <label htmlFor={combineId} className="text-sm">
              Combine into a transfer
            </label>
          </div>
          {effectiveCombine && (
            <div className="text-muted-foreground text-xs">
              Consumes the source rows and the inverse into one balanced
              transfer.
            </div>
          )}
          {!sourcesCombinable && (
            <div className="text-muted-foreground text-xs">
              Combining requires every source row to be nonzero and fully
              unallocated.
            </div>
          )}
          {foreignSplitDestination && (
            <div className="text-muted-foreground text-xs">
              A {currency} transfer can only land in one account, but this
              selection&apos;s inflow and the target account would be two —
              so these rows cannot be combined here.
            </div>
          )}
          {requiredFields.length > 0 && (
            <div className="text-muted-foreground text-xs">
              Transfers require the{' '}
              {requiredFields.map((field) => `"${field.name}"`).join(', ')}{' '}
              field, which this dialog cannot collect — create the inverse
              here, then combine through Create transaction.
            </div>
          )}
          {error && <div className="text-destructive text-xs">{error}</div>}
        </form>
      </div>
    </Modal>
  );
};
